import { useQueryClient } from '@tanstack/react-query';

import {
  getApiV1SerieQueryOptions,
  usePostApiV1Serie,
  usePutApiV1SerieId,
} from '@/shared/api/generated';
import type { SerieFormValues } from '../types/serieForm';

/** Callbacks do salvamento de Série. */
export interface SaveSerieCallbacks {
  readonly onSuccess?: () => void;
  readonly onError?: () => void;
}

/**
 * Hook para criar/editar uma Série.
 *
 * Encapsula os mutations gerados pelo Kubb (`POST /api/v1/serie` e
 * `PUT /api/v1/serie/{id}`, ambos multipart/form-data), escolhendo a operação
 * pela presença de `id` e invalidando o cache da listagem no sucesso.
 *
 * Mapeia `SerieFormValues` (camelCase, estado do formulário) para o payload
 * PascalCase esperado pelo contrato gerado — os dois não têm o mesmo shape.
 * `classStudents` (exibição local) é reduzido a `ClassStudentIds` (só os IDs);
 * não existe endpoint de vínculo incremental turma↔série, então a lista
 * completa vai sempre junto no salvamento da série.
 *
 * @example
 * ```tsx
 * const { save, isPending } = useSaveSerie();
 * save(values, editingId, { onSuccess, onError });
 * ```
 */
export function useSaveSerie() {
  const queryClient = useQueryClient();
  const createMutation = usePostApiV1Serie();
  const updateMutation = usePutApiV1SerieId();

  const invalidate = () => queryClient.invalidateQueries(getApiV1SerieQueryOptions());

  /**
   * Salva uma série. Cria quando `id` é `undefined`; caso contrário, atualiza.
   */
  const save = (
    values: SerieFormValues,
    id: number | undefined,
    callbacks?: SaveSerieCallbacks,
  ) => {
    const onSuccess = () => {
      void invalidate();
      callbacks?.onSuccess?.();
    };
    const onError = () => callbacks?.onError?.();

    const classStudentIds = values.classStudents.map(cs => cs.classStudentId);

    if (id === undefined) {
      createMutation.mutate(
        {
          data: {
            ...(values.companyId !== undefined ? { CompanyId: values.companyId } : {}),
            Title: values.title,
            Resumee: values.description,
            Active: values.active,
            IsGeneral: values.general,
            ...(values.thumbnailFile ? { Thumbnail: values.thumbnailFile } : {}),
            ClassStudentIds: classStudentIds,
          },
        },
        { onSuccess, onError },
      );
    } else {
      updateMutation.mutate(
        {
          id,
          data: {
            ...(values.companyId !== undefined ? { CompanyId: values.companyId } : {}),
            Title: values.title,
            Resumee: values.description,
            Active: values.active,
            IsGeneral: values.general,
            ...(values.thumbnailFile ? { Thumbnail: values.thumbnailFile } : {}),
            ...(values.removeThumbnail ? { RemoveThumbnail: true } : {}),
            ClassStudentIds: classStudentIds,
          },
        },
        { onSuccess, onError },
      );
    }
  };

  return {
    save,
    isPending: createMutation.isPending || updateMutation.isPending,
  } as const;
}
